import React, { useState } from "react";
import { toast } from "sonner";
import { fmtMoney, fmtSigned, fmtDate, fmtPct } from "../lib/format";
import { ArrowUpRight, Minus, AlertTriangle, X, Zap } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { orderRoll } from "../lib/api";
import { pollOrderStatus } from "../lib/orderStatus";
import { TradeConfirmDialog } from "./TradeConfirmDialog";

export const RollRecommendation = ({ analysis, onExecuteRoll, onClose }) => {
  const { user, refresh } = useAuth();
  const [confirm, setConfirm] = useState(null); // {mode, keep_old} or null
  const [submitting, setSubmitting] = useState(false);

  if (!analysis) {
    return (
      <div className="panel" data-testid="roll-rec-empty">
        <div className="panel-h">
          <div className="label-cap">Roll Recommendation</div>
        </div>
        <div className="p-6 text-center text-xs text-[#71717A]">
          Select a candidate from the chain to evaluate the roll.
        </div>
      </div>
    );
  }

  const { metrics, current_option, new_option, position, underlying_price } = analysis;
  const { category, net_credit, strike_delta, strike_vs_underlying, dte_delta, score, total_premium_after_roll, new_breakeven, credit_pct, week_n } = metrics;

  const categoryMeta = {
    credit_strong:   { label: "STRONG CREDIT ROLL",  color: "#00C853", icon: ArrowUpRight,    verdict: "HIGH SCORE" },
    credit_light:    { label: "LIGHT CREDIT ROLL",   color: "#00E676", icon: ArrowUpRight,    verdict: "POSITIVE" },
    credit_marginal: { label: "MARGINAL CREDIT",     color: "#FFD60A", icon: Minus,           verdict: "MARGINAL" },
    debit:           { label: "NET DEBIT ROLL",      color: "#FF3B30", icon: AlertTriangle,   verdict: "NEGATIVE" },
  }[category];

  const Icon = categoryMeta.icon;
  const original_breakeven = position.strike + position.premium_received;
  const breakeven_change = new_breakeven - original_breakeven;
  const roi = (total_premium_after_roll / underlying_price) * 100;

  return (
    <div className="panel" data-testid="roll-rec-panel">
      <div className="panel-h">
        <div className="label-cap">Roll Recommendation</div>
        <div className="flex items-center gap-2">
          <div
            className="text-[10px] font-bold tracking-widest uppercase px-2 py-1"
            style={{ background: categoryMeta.color, color: "#000" }}
            data-testid="roll-verdict"
          >
            {categoryMeta.verdict}
          </div>
          {onClose && (
            <button
              data-testid="roll-rec-close-btn"
              onClick={onClose}
              title="Close recommendation"
              aria-label="Close recommendation"
              className="text-[#71717A] hover:text-white transition-colors p-1 -mr-1"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div className="flex items-center gap-3">
          <Icon size={28} style={{ color: categoryMeta.color }} strokeWidth={2} />
          <div>
            <div className="text-base font-bold tracking-tight" style={{ color: categoryMeta.color }}>
              {categoryMeta.label}
            </div>
            <div className="text-[11px] text-[#A1A1AA] font-mono">
              {position.symbol} ${current_option.strike.toFixed(2)}C → ${new_option.strike.toFixed(2)}C
            </div>
          </div>
        </div>

        {/* Score bar */}
        <div data-testid="roll-score">
          <div className="flex justify-between items-baseline mb-1">
            <span className="label-cap">Roll Score</span>
            <span className="font-mono text-xl font-semibold">{score}<span className="text-[#71717A] text-xs">/100</span></span>
          </div>
          <div className="h-1.5 bg-[#1A1A1A] relative">
            <div
              className="h-full transition-all duration-500"
              style={{ width: `${score}%`, background: categoryMeta.color }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-px bg-[#27272A] border border-[#27272A]">
          <Stat label="Net Credit" value={fmtSigned(net_credit)} tone={net_credit >= 0 ? "green" : "red"} testid="stat-net-credit" />
          <Stat label={`Credit % · W${week_n}`} value={`${credit_pct >= 0 ? "+" : ""}${credit_pct.toFixed(2)}%`} tone={credit_pct > 0 ? "green" : "red"} testid="stat-credit-pct" />
          <Stat label="Strike Δ (vs current)" value={fmtSigned(strike_delta)} tone={strike_delta > 0 ? "green" : strike_delta < 0 ? "amber" : "neutral"} testid="stat-strike-delta" />
          <Stat label="Strike vs Underlying" value={fmtSigned(strike_vs_underlying)} tone={strike_vs_underlying >= 0 ? "green" : "amber"} testid="stat-strike-vs-underlying" />
          <Stat label="DTE Δ" value={`${dte_delta >= 0 ? "+" : ""}${dte_delta}d`} tone="neutral" testid="stat-dte-delta" />
          <Stat label="Current Opt Mid" value={fmtMoney(current_option.mid)} tone="neutral" testid="stat-current-mid" />
        </div>

        <div className="border border-[#27272A] p-3 bg-[#0A0A0A]">
          <div className="label-cap mb-2">New Option Breakeven</div>
          <div className="font-mono text-xs flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-[#A1A1AA]">Strike</span>
            <span>{fmtMoney(new_option.strike)}</span>
            <span className="text-[#71717A]">+</span>
            <span className="text-[#A1A1AA]">Premium</span>
            <span>{fmtMoney(new_option.mid)}</span>
          </div>
          <div className="mt-1.5 flex items-baseline justify-between font-mono">
            <span className="text-[#71717A] text-xs">=</span>
            <span className="font-semibold text-base">{fmtMoney(new_breakeven)}</span>
          </div>
        </div>

        {category === "debit" && (
          <div className="border border-[#FF3B30] bg-[rgba(255,59,48,0.06)] p-3 flex gap-2" data-testid="debit-warning">
            <AlertTriangle size={14} className="text-[#FF3B30] mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-[#FFD9D6] leading-relaxed">
              This roll requires <span className="font-bold">paying ${Math.abs(net_credit).toFixed(2)}/share</span> in premium.
              Per rule: <em>never make green when you pay</em>. Consider waiting or choosing a different strike/expiration.
            </p>
          </div>
        )}

        {onExecuteRoll && (
          <div className="border-t border-[#27272A] pt-3 space-y-2">
            <div className="label-cap">Log this roll</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="exec-roll-replace-btn"
                onClick={() => onExecuteRoll({ keep_old: false })}
                className="btn-accent text-xs py-2"
              >
                Replace position
              </button>
              <button
                data-testid="exec-roll-keep-btn"
                onClick={() => onExecuteRoll({ keep_old: true })}
                className="btn-outline text-xs py-2"
              >
                Keep + add new
              </button>
            </div>
            <p className="text-[10px] text-[#71717A] leading-relaxed">
              These buttons only update your ROLL.OPS tracker and roll history — ROLL.OPS does{" "}
              <strong className="text-white">not</strong> place real trades. Enter the actual
              order in your own brokerage account.
            </p>
            {user?.trading_enabled && user?.tradier_connected && (
              <div className="space-y-1">
                <button
                  data-testid="exec-roll-tradier-btn"
                  onClick={() => setConfirm({ mode: user.trade_mode || "paper", keep_old: false })}
                  className={`w-full py-2 text-xs font-bold uppercase tracking-widest border flex items-center justify-center gap-1.5 transition-colors ${
                    user.trade_mode === "live"
                      ? "border-[#FF3B30] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.1)]"
                      : "border-[#00E676] text-[#00E676] hover:bg-[rgba(0,230,118,0.1)]"
                  }`}
                >
                  <Zap size={12} />
                  {user.trade_mode === "live"
                    ? `Submit Roll to Tradier · ${(user.tradier_env || "").toUpperCase()}`
                    : "Simulate Roll (no Tradier call)"}
                </button>
                {user.trade_mode !== "live" && (
                  <p className="text-[9px] text-[#FFD60A] leading-relaxed pl-1">
                    Flip the header toggle to <strong>SUBMIT</strong> to route this order to your Tradier {user.tradier_env} account.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <TradeConfirmDialog
        open={!!confirm}
        mode={confirm?.mode}
        title="Roll Position"
        summary={
          <>
            Roll <span className="font-bold text-white">{position.symbol}</span> from{" "}
            <span className="text-white">${position.strike.toFixed(2)}C {fmtDate(position.expiration)}</span> to{" "}
            <span className="text-white">${new_option.strike.toFixed(2)}C {fmtDate(new_option.expiration)}</span>.
          </>
        }
        details={[
          { label: "Symbol", value: position.symbol },
          { label: "Buy to close", value: `$${position.strike.toFixed(2)}C · ${position.expiration}` },
          { label: "Sell to open", value: `$${new_option.strike.toFixed(2)}C · ${new_option.expiration}` },
          { label: "Contracts", value: position.contracts || 1 },
          { label: "Expected net credit", value: fmtSigned(net_credit) },
        ]}
        busy={submitting}
        onCancel={() => !submitting && setConfirm(null)}
        onConfirm={async () => {
          setSubmitting(true);
          try {
            const r = await orderRoll({
              position_id: position.id,
              new_strike: new_option.strike,
              new_expiration: new_option.expiration,
              limit_price: typeof net_credit === "number" ? Number(net_credit.toFixed(2)) : null,
            });
            toast.success(
              r.mode === "live"
                ? `Submitted to Tradier · order ${r.tradier_order_id || "queued"}`
                : "SIMULATE mode — no order was sent to Tradier. Flip header toggle to SUBMIT for real orders."
            );
            // Live orders: poll for terminal status so the user sees fills/rejects
            if (r.mode === "live" && r.order_id) {
              pollOrderStatus({
                orderId: r.order_id,
                onUpdate: (s) => {
                  if (["filled", "canceled", "cancelled", "rejected", "expired"].includes(String(s.status).toLowerCase())) {
                    toast[s.status === "filled" ? "success" : "warning"](`Roll ${s.status}${s.avg_fill_price ? ` @ $${Number(s.avg_fill_price).toFixed(2)}` : ""}`);
                  }
                },
              });
            }
            setConfirm(null);
            await refresh?.();
          } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(detail ? String(detail).slice(0, 300) : "Roll submit failed", { duration: 12000 });
          } finally { setSubmitting(false); }
        }}
      />
    </div>
  );
};

const toneClass = {
  green: "text-[#00E676]",
  red: "text-[#FF3B30]",
  amber: "text-[#FF9F0A]",
  neutral: "text-white",
};

const Stat = ({ label, value, tone = "neutral", testid }) => (
  <div className="bg-[#121212] p-3" data-testid={testid}>
    <div className="label-cap">{label}</div>
    <div className={`font-mono text-base mt-1 ${toneClass[tone]}`}>{value}</div>
  </div>
);
